/*
 *   Copyright 2022  SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// noinspection ES6UnusedImports eslint-disable-next-line @typescript-eslint/no-unused-vars
import { h, Host } from "@stencil/core";
export class DiscoveryInputChips {
    constructor() {
        this.change_handler_enabled = true;
        this.autocomplete_debounce = 200;
        this.autocomplete_highlight = true;
        this.delimiters = [' '];
        this.boundClickHandler = this.handleDocumentClick.bind(this);
        this.chips = [];
        this.autocomplete = undefined;
        this.constrain_input = false;
        this.value = undefined;
        this.innerChips = [];
    }
    updateChips() {
        this.innerChips = (this.chips || []).slice();
    }
    componentDidLoad() {
        this.el.addEventListener('click', () => this.real_input.focus());
        this.innerChips = (this.chips || []).slice();
    }
    handleAutocompleteContainerFocus(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
    }
    handleChipClose(event) {
        const chipLabel = event.detail;
        let index = -1;
        for (let i = 0; i < this.innerChips.length; i++) {
            if (this.innerChips[i] === chipLabel) {
                index = i;
                break;
            }
        }
        if (index >= 0) {
            void this.deleteChip(index);
        }
    }
    handleDocumentClick(event) {
        if (event.path.includes(this.el)) {
            return;
        }
        this.closeAutoComplete(true);
    }
    handleChipClick(event, chip) {
        this.chipClick.emit({
            label: chip.label,
            event: event
        });
    }
    handleInput(event) {
        let autocomplete_items = [];
        this.value = this.real_input.value;
        const key = event ? event.data || '' : '';
        if (this.delimiters.includes(key)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (!this.constrain_input)
                return this.createChip(undefined);
        }
        if (this.autocomplete_debounce_key) {
            clearTimeout(this.autocomplete_debounce_key);
        }
        this.chipInput.emit();
        return new Promise(resolve => {
            this.autocomplete_debounce_key = setTimeout(() => {
                (async () => {
                    this.autocomplete_debounce_key = null;
                    const value = this.real_input.value;
                    this.highlighted_autocomplete_index = null;
                    if (this.autocomplete) {
                        autocomplete_items = await this.autocomplete(value);
                    }
                    if (!autocomplete_items.length) {
                        this.closeAutoComplete(false);
                        return;
                    }
                    else {
                        this.showAutoComplete(autocomplete_items, value);
                    }
                })().then(() => resolve()).catch(e => console.error(e));
            }, this.autocomplete_debounce);
        });
    }
    async handleBeforeInput(event) {
        const input_type = event.inputType;
        const key = event.data;
        let autocomplete_items = [];
        if (input_type === 'deleteContentBackward') {
            if (this.real_input.selectionStart === 0) {
                if (this.innerChips.length)
                    await this.deleteChip(this.innerChips.length - 1);
            }
            return;
        }
        if ((input_type === 'insertLineBreak')) {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (this.highlighted_autocomplete_index !== null) {
                const div = this.autocompleteContainer.childNodes[this.highlighted_autocomplete_index];
                return this.handleAutoCompleteItemSelected(div);
            }
            else {
                if (this.autocomplete_select_default) {
                    if (this.autocompleteContainer.childNodes.length) {
                        const div = this.autocompleteContainer.childNodes[0];
                        return this.handleAutoCompleteItemSelected(div);
                    }
                }
            }
            await this.createChip(undefined);
            return;
        }
        if (this.constrain_input && !!this.autocomplete) {
            let value = this.real_input.value;
            value += key;
            this.highlighted_autocomplete_index = null;
            if (!!this.autocomplete) {
                autocomplete_items = await this.autocomplete(value);
            }
            if (!autocomplete_items.length) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
            return;
        }
    }
    handleChange() {
        if (!this.change_handler_enabled) {
            return;
        }
    }
    handleKeydown(event) {
        const key = event.key;
        let navigating = false;
        if (key === 'ArrowDown') {
            if (this.highlighted_autocomplete_index == null)
                this.highlighted_autocomplete_index = -1;
            this.highlighted_autocomplete_index++;
            if (this.highlighted_autocomplete_index > (this.autocompleteContainer.childNodes.length - 1))
                this.highlighted_autocomplete_index = this.autocompleteContainer.childNodes.length - 1;
            navigating = true;
        }
        if (key === 'ArrowUp') {
            if (this.highlighted_autocomplete_index == null)
                this.highlighted_autocomplete_index = 1;
            this.highlighted_autocomplete_index--;
            if (this.highlighted_autocomplete_index < 0)
                this.highlighted_autocomplete_index = 0;
            navigating = true;
        }
        if (navigating) {
            const items = this.autocompleteContainer.children;
            for (let i = 0; i < items.length; i++) {
                const item = items.item(i);
                item.style.backgroundColor = 'var(--chip-input-autocomplete-background-color, white)';
                if (this.highlighted_autocomplete_index === i) {
                    item.style.backgroundColor = 'var(--chip-input-autocomplete-hover-background-color, lightblue)';
                    item.scrollIntoView();
                }
            }
        }
    }
    updateCaretPosition() {
        const selection_start = this.real_input.selectionStart;
        this.caret_position_tracker.textContent = this.real_input.value.substring(0, selection_start).replace(/\s/g, '\u00a0');
        const pos_rect = this.caret_position_tracker.getBoundingClientRect();
        const input_rect = this.real_input.getBoundingClientRect();
        this.caret_position = {
            x: input_rect.x + pos_rect.width,
            y: input_rect.y + pos_rect.height
        };
    }
    closeAutoComplete(force) {
        if (!force && this.show_autocomplete_on_focus)
            return;
        if (this.autocomplete_dismiss_target) {
            this.autocomplete_dismiss_target.removeEventListener('click', this.boundClickHandler);
        }
        else {
            document.removeEventListener('click', this.boundClickHandler);
        }
        this.autocompleteContainer.style.display = 'none';
    }
    async deleteChip(index) {
        this.innerChips.splice(index, 1);
        this.innerChips = [...this.innerChips];
        this.chipChange.emit(this.innerChips);
        if (this.show_autocomplete_on_focus && this.autocomplete) {
            await this.handleInput(undefined);
        }
    }
    async handleAutoCompleteItemSelected(div) {
        this.change_handler_enabled = false;
        this.real_input.value = div.dataset.value;
        await this.createChip(div.autocomplete_data);
        this.closeAutoComplete(false);
        this.real_input.blur();
        this.real_input.focus();
        this.highlighted_autocomplete_index = null;
    }
    async handleFocus() {
        this.updateCaretPosition();
        if (this.show_autocomplete_on_focus && !!this.autocomplete) {
            const autocomplete_items = await this.autocomplete(this.real_input.value);
            this.showAutoComplete(autocomplete_items, this.real_input.value);
        }
    }
    showAutoComplete(autocomplete_items, highlight_value) {
        const rect = this.real_input.getBoundingClientRect();
        const value = highlight_value;
        this.autocompleteContainer.style.display = 'block';
        this.autocompleteContainer.style.top = `${rect.height + 3}px`;
        this.autocompleteContainer.style.left = `${this.real_input.offsetLeft}px`;
        this.autocompleteContainer.innerHTML = '';
        autocomplete_items.map(item => {
            let label;
            if (typeof item == 'string') {
                label = item;
            }
            else {
                label = item.label;
            }
            const start_index = label.toLowerCase().indexOf(value.toLowerCase());
            const prefix = label.substring(0, start_index);
            const match = label.substr(start_index, value.length);
            const postfix = label.substr(start_index + value.length);
            const div = document.createElement('DIV');
            div.addEventListener('focus', (event) => {
                event.preventDefault();
                event.stopImmediatePropagation();
            });
            div.style.backgroundColor = 'var(--chip-input-autocomplete-background-color, white)';
            div.style.borderBottom = '1px solid lightgrey';
            div.style.padding = '3px';
            div.style.cursor = 'pointer';
            if (this.autocomplete_highlight) {
                div.innerHTML = `${prefix}<span style='font-weight: bold'>${match}</span>${postfix}`;
            }
            else {
                div.innerHTML = label;
            }
            div.dataset.value = label;
            div.onmouseover = () => {
                div.style.backgroundColor = 'var(--chip-input-autocomplete-hover-background-color, lightblue)';
            };
            div.onmouseout = () => {
                div.style.backgroundColor = 'var(--chip-input-autocomplete-background-color, white)';
            };
            div.onclick = () => {
                void this.handleAutoCompleteItemSelected(div);
            };
            this.autocompleteContainer.appendChild(div);
        });
        let autocomplete_dismiss_target = this.autocompleteContainer;
        let element;
        if (this.autocomplete_dismiss_target) {
            if (typeof this.autocomplete_dismiss_target == 'string') {
                element = this.autocompleteContainer.querySelector(this.autocomplete_dismiss_target);
            }
            else {
                element = this.autocomplete_dismiss_target;
            }
        }
        if (element) {
            autocomplete_dismiss_target = element;
        }
        autocomplete_dismiss_target.addEventListener('click', this.boundClickHandler);
    }
    async createChip(value) {
        if (!value) {
            value = this.real_input.value;
        }
        if (value.trim() !== '') {
            this.innerChips = [...this.innerChips, value.trim()];
            this.change_handler_enabled = false;
            this.real_input.value = '';
            this.change_handler_enabled = true;
            this.chipCreate.emit(value.trim());
            this.chipChange.emit(this.innerChips);
        }
        if (this.show_autocomplete_on_focus && this.autocomplete) {
            this.updateCaretPosition();
            await this.handleInput();
        }
        else if (this.autocomplete) {
            this.closeAutoComplete(false);
        }
    }
    render() {
        return h(Host, { key: '14b1a13356122245334e0fd661ce5af65f64933a' }, h("div", { key: 'af52585ed0bf08d75a6bf54f4006066ffc157667', class: "chip-input-autocomplete-container", onFocus: this.handleAutocompleteContainerFocus.bind(this), ref: e => this.autocompleteContainer = e }), h("div", { key: '0afb1a2bac33f32771ff8cb46ce9f0ec5af0d2e6', class: "wrapper" }, this.innerChips.map(chip => h("discovery-input-chips-chip", { onClick: event => this.handleChipClick(event, chip), label: chip, onRemoveChip: this.handleChipClose.bind(this) })), h("div", { key: 'e45f85d7a7bfc69de4f4f2dd300476846b42120a', class: "caret_position_tracker", ref: el => this.caret_position_tracker = el }), h("input", { key: 'd78633927fa76e8bac9c6d516d9e4642eb34fbcd', class: "real_input", type: "text", ref: e => {
                this.real_input = e;
                e.addEventListener('beforeinput', this.handleBeforeInput.bind(this));
            }, onInput: this.handleInput.bind(this), onChange: this.handleChange.bind(this), onKeyDown: this.handleKeydown.bind(this), onKeyUp: this.updateCaretPosition.bind(this), onClick: this.updateCaretPosition.bind(this), onFocus: this.handleFocus.bind(this) })));
    }
    static get is() { return "discovery-input-chips"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-input-chips.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-input-chips.css"]
        };
    }
    static get properties() {
        return {
            "chips": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "string[]",
                    "resolved": "string[]",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "defaultValue": "[]"
            },
            "autocomplete": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(value: string) => Promise<any>",
                    "resolved": "(value: string) => Promise<any>",
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                }
            },
            "constrain_input": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "constrain_input",
                "reflect": false,
                "defaultValue": "false"
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "value",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "innerChips": {}
        };
    }
    static get events() {
        return [{
                "method": "chipClick",
                "name": "chipClick",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "chipInput",
                "name": "chipInput",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "chipChange",
                "name": "chipChange",
                "bubbles": true,
                "cancelable": false,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "string[]",
                    "resolved": "string[]",
                    "references": {}
                }
            }, {
                "method": "chipCreate",
                "name": "chipCreate",
                "bubbles": true,
                "cancelable": false,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "chips",
                "methodName": "updateChips"
            }];
    }
    static get listeners() {
        return [{
                "name": "document:click",
                "method": "handleDocumentClick",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=discovery-input-chips.js.map
